import React, { Fragment, useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
    Row,
    Col,
    Card,
    CardBody,
    CardHeader,
    Button,
    Form,
    Input,
} from "reactstrap";
import DataTable from "react-data-table-component";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import { ToastContainer, toast } from "react-toastify";
import Breadcrumbs from "../../Components/Common/Breadcrumb";
import AxiosInstance from "../../services/AxiosInstance";
import Spinner from "../../Components/Common/Spinner";
import NoDataFound from "../../Components/Common/NoDataFound";
import { customStyles } from "../../Constant";
import "react-toastify/dist/ReactToastify.css";
import { Edit2, Trash2 } from "react-feather";

const PAGE_SIZES = [10, 25, 50, 100];

// DB user_role_status: 1 = Active, 0 = Inactive
const ROLE_STATUS_OPTIONS = [
    { value: 1, label: "Active" },
    { value: 0, label: "Inactive" },
];

const ManageRoles = () => {
    const navigate = useNavigate();
    const userData = JSON.parse(localStorage.getItem("user_data") || "{}");
    const isSuperAdmin = userData.is_super === 1 || userData.is_super === "1";
    const isSuperDenied = userData.is_super === 0 || userData.is_super === "0";

    const [roles, setRoles] = useState([]);
    const [totalRows, setTotalRows] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [searchApplied, setSearchApplied] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [sortColumn, setSortColumn] = useState("user_role_title");
    const [sortDirection, setSortDirection] = useState("asc");
    const [updatingStatusId, setUpdatingStatusId] = useState(null);
    const [deletingRoleId, setDeletingRoleId] = useState(null);

    const animatedComponents = makeAnimated();
    const selectColors = {
        primary: "#007bff",
        primary25: "#cce5ff",
        approved: "green",
        rejected: "#6c757d",
        lightGray: "#f8f9fa",
        darkGray: "#343a40",
    };
    const customStylesSelect = {
        control: (provided, state) => ({
            ...provided,
            minWidth: "120px",
            borderColor: state.isFocused ? selectColors.primary : provided.borderColor,
            boxShadow: state.isFocused ? `0 0 0 1px ${selectColors.primary}` : provided.boxShadow,
        }),
        singleValue: (provided, state) => ({
            ...provided,
            color: state.data?.value === 1 ? selectColors.approved : selectColors.rejected,
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected ? (state.data.value === 1 ? selectColors.approved : selectColors.rejected) : state.isFocused ? selectColors.primary25 : selectColors.lightGray,
            color: state.isSelected || state.isFocused ? "#fff" : selectColors.darkGray,
        }),
    };

    const fetchRoles = useCallback(async (page, perPageSize, search, sortBy, sortDir) => {
        setLoading(true);
        setError(null);
        try {
            const res = await AxiosInstance().post("/admin/role/get_roles", {
                page,
                per_page: perPageSize,
                search: (search || "").trim() || undefined,
                sort_by: sortBy || "user_role_title",
                sort_dir: sortDir || "asc",
            });
            if (res.data.status && res.data.data != null) setRoles(Array.isArray(res.data.data) ? res.data.data : []);
            else setRoles([]);
            setTotalRows(typeof res.data?.total === "number" ? res.data.total : 0);
        } catch (e) {
            console.error(e);
            toast.error(e.response?.data?.message || "Failed to load roles");
            setRoles([]);
            setTotalRows(0);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (isSuperDenied) {
            setLoading(false);
            setError("Permission denied. Super Admin only.");
            return;
        }
        fetchRoles(currentPage, perPage, searchApplied, sortColumn, sortDirection);
    }, [isSuperDenied, currentPage, perPage, searchApplied, sortColumn, sortDirection, fetchRoles]);

    const loadData = useCallback(() => {
        fetchRoles(currentPage, perPage, searchApplied, sortColumn, sortDirection);
    }, [fetchRoles, currentPage, perPage, searchApplied, sortColumn, sortDirection]);

    const openCreate = () => navigate("/job/admin/manage-roles/new");
    const handleSearchSubmit = (e) => {
        e?.preventDefault();
        setSearchApplied(searchQuery);
        setCurrentPage(1);
    };
    const handlePageChange = (page) => setCurrentPage(page);
    const handlePerRowsChange = (newPerPage, page) => {
        setPerPage(newPerPage);
        setCurrentPage(page);
    };
    const handleSort = (selectedColumn, direction) => {
        const field = selectedColumn?.id || "user_role_title";
        setSortColumn(field);
        setSortDirection(direction === "asc" ? "asc" : "desc");
        setCurrentPage(1);
    };

    const handleDeleteRole = async (role) => {
        if (!window.confirm(`Delete role "${role.user_role_title || "this role"}"? This cannot be undone.`)) return;
        setDeletingRoleId(role.user_role_id);
        try {
            const res = await AxiosInstance().post(`/admin/role/delete/${role.user_role_id}`, {});
            if (res.data.status) {
                toast.success(res.data.message || "Role deleted");
                fetchRoles(currentPage, perPage, searchApplied, sortColumn, sortDirection);
            } else {
                toast.error(res.data.message || "Delete failed");
            }
        } catch (e) {
            toast.error(e.response?.data?.message || "Delete failed");
        } finally {
            setDeletingRoleId(null);
        }
    };

    const handleStatusChange = async (roleId, newValue) => {
        const userRoleStatus = newValue === 1 ? 1 : 0;
        setUpdatingStatusId(roleId);
        try {
            const res = await AxiosInstance().post("/admin/role/update_status", {
                user_role_id: roleId,
                user_role_status: userRoleStatus,
            });
            if (res.data.status) {
                toast.success(res.data.message || "Status updated");
                fetchRoles(currentPage, perPage, searchApplied, sortColumn, sortDirection);
            } else {
                toast.error(res.data.message || "Update failed");
            }
        } catch (e) {
            toast.error(e.response?.data?.message || "Update failed");
        } finally {
            setUpdatingStatusId(null);
        }
    };

    const tableColumns = [
        {
            name: "#",
            cell: (row, index) => (currentPage - 1) * perPage + index + 1,
            width: "60px",
            sortable: false,
        },
        {
            id: "user_role_title",
            name: "User Role",
            selector: (row) => row.user_role_title || "—",
            sortable: true,
            grow: 2,
            cell: (row) => <span className="fw-medium">{row.user_role_title || "—"}</span>,
        },
        {
            id: "user_role_status",
            name: "Status",
            width: "160px",
            center: true,
            cell: (row) => {
                const dbValue = row.user_role_status === "1" || row.user_role_status === 1 ? 1 : 0;
                const valueOption = ROLE_STATUS_OPTIONS.find((opt) => opt.value === dbValue) || ROLE_STATUS_OPTIONS[0];
                return (
                    <div className="d-flex align-items-center justify-content-center">
                        <Select
                            components={animatedComponents}
                            className="basic-single"
                            classNamePrefix="select"
                            value={valueOption}
                            onChange={(selectedOption) => selectedOption && handleStatusChange(row.user_role_id, selectedOption.value)}
                            options={ROLE_STATUS_OPTIONS}
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            styles={{
                                ...customStylesSelect,
                                menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
                            }}
                            isDisabled={updatingStatusId === row.user_role_id}
                            isSearchable={false}
                            menuPlacement="auto"
                        />
                    </div>
                );
            },
        },
        {
            name: "Actions",
            sortable: false,
            width: "100px",
            center: true,
            cell: (row) => (
                <div className="d-flex justify-content-center align-items-center gap-1">
                    <Link
                        to={`/job/admin/manage-roles/${row.user_role_id}/permissions`}
                        state={{ roleTitle: row.user_role_title }}
                        title="Edit Role"
                    >
                        <Edit2 size={18} className="edit-icon" />
                    </Link>
                    {!(row.is_system_fixed === 1 || row.is_system_fixed === "1") && (
                        <button
                            type="button"
                            className="btn btn-link p-0 text-danger border-0 bg-transparent"
                            onClick={() => handleDeleteRole(row)}
                            disabled={deletingRoleId === row.user_role_id}
                            title="Delete Role"
                        >
                            <Trash2 size={18} className="delete-icon" />
                        </button>
                    )}
                </div>
            ),
        },
    ];

    if (isSuperDenied && !loading) {
        return (
            <Fragment>
                <Breadcrumbs title="Manage Roles" mainTitle="Roles & Permissions" />
                <Row>
                    <Col xs="12">
                        <Card className="border-0 shadow-sm">
                            <CardBody className="text-center py-5">
                                <i className="fa fa-lock fa-3x text-warning mb-3" />
                                <h5 className="text-danger mb-2">Access Denied</h5>
                                <p className="text-muted mb-0">Only Super Admin can manage roles and permissions.</p>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
                <ToastContainer />
            </Fragment>
        );
    }

    if (loading && roles.length === 0) {
        return (
            <Fragment>
                <Breadcrumbs title="Manage Roles" mainTitle="Roles & Permissions" />
                <div className="d-flex justify-content-center align-items-center py-5">
                    <Spinner />
                </div>
            </Fragment>
        );
    }

    if (error && roles.length === 0) {
        return (
            <Fragment>
                <Breadcrumbs title="Manage Roles" mainTitle="Roles & Permissions" />
                <Row>
                    <Col xs="12">
                        <Card>
                            <CardBody className="text-center py-5">
                                <p className="text-danger mb-0">{error}</p>
                                <Button color="primary" className="mt-3" onClick={() => window.location.reload()}>
                                    Retry
                                </Button>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
                <ToastContainer />
            </Fragment>
        );
    }

    return (
        <Fragment>
            <Breadcrumbs title="Manage Roles" mainTitle="Roles & Permissions" />
            <Row>
                <Col xs="12" className="box-col-12 xl-100">
                    <Card>
                        <CardHeader className="py-3">
                            <div className="d-flex flex-wrap justify-content-between align-items-end mb-3">
                                <Form className="theme-form me-2 flex-grow-1" onSubmit={handleSearchSubmit}>
                                    <div className="input-group flex-nowrap" style={{ height: 35, maxWidth: "350px" }}>
                                        <Input
                                            className="form-control"
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            type="search"
                                            placeholder="Search by role name..."
                                        />
                                        <Button color="primary" type="submit" className="btn-primary input-group-text pe-3 ps-3">
                                            Search
                                        </Button>
                                    </div>
                                </Form>
                                <div className="d-flex align-items-center ms-auto mt-3 mt-md-0 gap-2">
                                    <Button color="primary" onClick={openCreate} className="d-flex align-items-center">
                                        <i className="fa fa-plus me-1" /> Add Role
                                    </Button>
                                    <Button color="primary" outline onClick={loadData} className="d-flex align-items-center">
                                        <i className="fa fa-sync-alt me-1" /> Refresh
                                    </Button>
                                </div>
                            </div>
                        </CardHeader>

                        <CardBody className="px-0 py-3">
                            <div className="table-container">
                                <DataTable
                                    responsive
                                    customStyles={customStyles}
                                    data={roles}
                                    columns={tableColumns}
                                    striped
                                    pagination
                                    paginationServer
                                    sortServer
                                    onSort={handleSort}
                                    defaultSortFieldId="user_role_title"
                                    defaultSortAsc={true}
                                    paginationTotalRows={totalRows}
                                    paginationPerPage={perPage}
                                    paginationRowsPerPageOptions={PAGE_SIZES}
                                    onChangePage={handlePageChange}
                                    onChangeRowsPerPage={handlePerRowsChange}
                                    progressPending={loading}
                                    progressComponent={<Spinner />}
                                    noDataComponent={<NoDataFound message="No roles found" />}
                                />
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            <ToastContainer />
        </Fragment>
    );
};

export default ManageRoles;
